import type {
  Filter,
  FilterDataMultiSelect,
  FilterDataOption,
  FilterDataRadio,
  LabelComponent,
} from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { AccommodationRating } from '@/app/components/search/utils/api/filtersAPIResponseTypes.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import { hasAnyTripAdvisorSource } from '@/app/common/reviews/reviewSource.ts';
import { TA_RATING_FILTER_TRANSLATION_KEYS } from '@/app/common/reviews/taRatingFilterKeys.ts';
import { counterOf, disabledAtZero } from '@/app/common/filters/groups/optionCounts.ts';
import {
  ANY_RATING_VALUE,
  EVO_RATING_BUCKETS,
  RATING_VALUE,
  evoSelectedRating,
} from '@/app/common/filters/core/ratingValues.ts';

type DefaultRating = {
  value: RATING_VALUE;
  translationKey: string;
};
const defaultRatings: DefaultRating[] = [
  { value: RATING_VALUE.EXCELLENT, translationKey: 'vacation.oss.filters.rating.excellent' },
  { value: RATING_VALUE.VERY_GOOD, translationKey: 'vacation.oss.filters.rating.verygood' },
  { value: RATING_VALUE.GOOD, translationKey: 'vacation.oss.filters.rating.good' },
  { value: RATING_VALUE.PLEASANT, translationKey: 'vacation.oss.filters.rating.fair' },
  // { value: RATING_VALUE.POOR, translationKey: 'vacation.oss.filters.rating.poor' },
];

type CreateRatingFilterOption = {
  defaultRating: DefaultRating;
  selectedValues: string[];
  accommodationRating?: AccommodationRating;
  translationDictionary: TranslationDictionary;
};
const createRatingFilterOption = ({
  defaultRating,
  selectedValues,
  accommodationRating,
  translationDictionary,
}: CreateRatingFilterOption): FilterDataOption => {
  const labelComponent: LabelComponent = {
    componentName: 'rating',
    props: {
      value: defaultRating.value,
      translationKey: defaultRating.translationKey,
    },
  };
  const selected = selectedValues.includes(defaultRating.value);
  const counter = counterOf(accommodationRating, defaultRating.value);

  return {
    label: getTranslationServer(defaultRating.translationKey, translationDictionary),
    labelComponent,
    selected,
    disabled: disabledAtZero(selected, counter),
    value: defaultRating.value,
    counter,
  };
};

type CreateRatingFilterOptions = {
  ratings: DefaultRating[];
  selectedValues: string[];
  accommodationRating?: AccommodationRating;
  translationDictionary: TranslationDictionary;
};
const createRatingFilterOptions = ({
  ratings,
  selectedValues,
  accommodationRating,
  translationDictionary,
}: CreateRatingFilterOptions): FilterDataOption[] =>
  ratings.map((defaultRating) =>
    createRatingFilterOption({
      defaultRating,
      selectedValues,
      accommodationRating,
      translationDictionary,
    })
  );

const ratingSelectionLabel = (score: number, translationDictionary: TranslationDictionary): string =>
  getTranslationServer('vacation.oss.filters.rating.selected_rating', translationDictionary).replace(
    '__number__',
    score.toFixed(1)
  );

// A response carrying none of the evo keys has not been told about them, which is not the same as
// every bucket holding no hotels; counting it as zero would disable the whole list at once.
const evoCounters = (accommodationRating?: AccommodationRating): AccommodationRating | undefined =>
  accommodationRating && EVO_RATING_BUCKETS.some(({ value }) => Object.hasOwn(accommodationRating, value))
    ? accommodationRating
    : undefined;

type CreateEvoRatingOptions = {
  selectedValue: string;
  accommodationRating?: AccommodationRating;
  translationDictionary: TranslationDictionary;
};
const createEvoRatingOptions = ({
  selectedValue,
  accommodationRating,
  translationDictionary,
}: CreateEvoRatingOptions): FilterDataOption[] => [
  {
    label: getTranslationServer('vacation.oss.filters.rating.any_rating', translationDictionary),
    selected: selectedValue === ANY_RATING_VALUE,
    isDefault: true,
    value: ANY_RATING_VALUE,
  },
  ...EVO_RATING_BUCKETS.map(({ value, score }) => {
    const selected = selectedValue === value;
    const counter = counterOf(accommodationRating, value);

    return {
      label: ratingSelectionLabel(score, translationDictionary),
      labelComponent: { componentName: 'ratingEvo', props: { score } },
      selected,
      disabled: disabledAtZero(selected, counter),
      value,
      counter,
    };
  }),
];

type CreateRatingFilters = {
  searchParams: SearchParams;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
  productsData: ProductsData;
  useEvoFilters?: boolean;
};
export const createRatingFilters = ({
  searchParams,
  dynamicFiltersResponse,
  translationDictionary,
  productsData,
  useEvoFilters,
}: CreateRatingFilters): Filter[] => {
  // KNOWN GAP (MPH-742): bucket counters come from the backend `accommodationRating` and the
  // filter itself is applied server-side, both source-blind — a hotel whose source is not
  // TRUSTYOU/TRIPADVISOR is still counted and filterable here even though its card shows no
  // score. Supply is expected to stop emitting those sources; until then a filtered list can
  // include cards without a visible rating.

  if (useEvoFilters) {
    const selectedValue = evoSelectedRating(searchParams[URLParameters.RATING]) ?? ANY_RATING_VALUE;

    return [
      {
        name: FILTER_NAME.RATING,
        type: FILTER_TYPE.RADIO,
        data: {
          options: createEvoRatingOptions({
            selectedValue,
            accommodationRating: evoCounters(dynamicFiltersResponse?.accommodationRating),
            translationDictionary,
          }),
          defaultValue: ANY_RATING_VALUE,
        } satisfies FilterDataRadio,
      },
    ];
  }

  const selectedValuesFromUrl = searchParams[URLParameters.RATING]?.split(',') || [];
  const selectedValues = selectedValuesFromUrl.length > 0 ? selectedValuesFromUrl : [ANY_RATING_VALUE];

  // Same page-level rule as the option rows (RatingLabel): any TripAdvisor hotel in the result
  // list switches the bucket wording to the TA taxonomy; buckets and thresholds stay unchanged.
  const isTripAdvisor = hasAnyTripAdvisorSource(
    (productsData?.dealCards ?? []).map((dealCard) => dealCard.reviewSource)
  );
  const ratings = isTripAdvisor
    ? defaultRatings.map((rating) => ({
        ...rating,
        translationKey: TA_RATING_FILTER_TRANSLATION_KEYS[rating.value] ?? rating.translationKey,
      }))
    : defaultRatings;

  const options = createRatingFilterOptions({
    ratings,
    selectedValues,
    accommodationRating: dynamicFiltersResponse?.accommodationRating,
    translationDictionary,
  });
  const ratingFilter: Filter = {
    name: FILTER_NAME.RATING,
    type: FILTER_TYPE.MULTI_SELECT,
    data: {
      options,
    } as FilterDataMultiSelect,
  };

  return [...[ratingFilter]].filter((f) => !!f);
};
